import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Check, Star, Package, ArrowRight, 
  Shield, Users, CreditCard, ChevronRight, HelpCircle, Send, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { packsData, packsFaq } from '../../data/packs';
import { packsDataEN, packsFaqEN } from '../../data/packsEN';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import StickyWhatsAppMobile from '../landing/StickyWhatsAppMobile';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { WHATSAPP_BASE_URL, getGenericWhatsAppLink } from '../../utils/whatsapp';

// WhatsApp SVG Icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const PacksPage = () => {
  const location = useLocation();
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  const langPrefix = isEnglish ? '/en' : '';
  
  // Use English or French data based on language
  const packs = isEnglish ? packsDataEN : packsData;
  
  // WhatsApp link with correct number
  const whatsappLink = getGenericWhatsAppLink(language);
  
  // Current FAQ based on language
  const currentFaq = isEnglish ? packsFaqEN : packsFaq;

  // Scroll to top on page load / route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Generate WhatsApp link for pack booking (NO EMOJIS)
  const getPackWhatsAppLink = (packName) => {
    const message = isEnglish 
      ? `Hello,
I would like to book the package: ${packName}

Preferred date:
Number of people:
Hotel:

Thank you.`
      : `Bonjour,
Je souhaite réserver le pack : ${packName}

Date souhaitée :
Nombre de personnes :
Hôtel :

Merci.`;
    return `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-12 md:py-20 bg-gradient-to-b from-cyan-50 to-white overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-100/40 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-teal-100/40 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-6 animate-in fade-in slide-in-from-bottom duration-500">
              <Link to={langPrefix || '/'} className="hover:text-teal-600 transition-colors">
                {isEnglish ? 'Home' : 'Accueil'}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-cyan-600">
                {isEnglish ? 'Excursion Packages' : "Packs d'excursions"}
              </span>
            </nav>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-slate-100 mb-8 animate-in fade-in slide-in-from-bottom duration-500 delay-100">
              <Package className="w-4 h-4 text-cyan-500" />
              <span className="text-sm text-slate-600 font-medium">
                {isEnglish ? 'Save and simplify' : 'Économisez et simplifiez'}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-6 animate-in fade-in slide-in-from-bottom duration-500 delay-200">
              {isEnglish ? 'Excursion Packages in' : "Packs d'excursions à"}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-teal-500">
                Hurghada
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-8 animate-in fade-in slide-in-from-bottom duration-500 delay-300">
              {isEnglish 
                ? 'Combine multiple excursions and enjoy great rates for an unforgettable stay.'
                : 'Combinez plusieurs excursions et profitez de tarifs avantageux pour vivre un séjour inoubliable.'}
            </p>

            <Button
              asChild
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 rounded-full shadow-xl shadow-emerald-200 transition-all duration-300 hover:shadow-emerald-300 hover:scale-105 animate-in fade-in slide-in-from-bottom duration-500 delay-400"
            >
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                {isEnglish ? 'Request a package' : 'Demander un pack'}
              </a>
            </Button>

            <p className="text-sm text-slate-400 mt-4 animate-in fade-in duration-500 delay-500">
              {isEnglish ? 'Single activity booking also available' : 'Réservation à l\'unité également possible'}
            </p>
          </div>
        </section>

        {/* Packs Cards */}
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {packs.map((pack, index) => {
                const whatsappPackLink = getPackWhatsAppLink(pack.name);
                
                return (
                  <div
                    key={pack.id}
                    className={`relative bg-white rounded-3xl overflow-hidden border-2 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl animate-in fade-in slide-in-from-bottom duration-500 ${
                      pack.popular
                        ? 'border-cyan-400 shadow-xl shadow-cyan-100'
                        : 'border-slate-100 hover:border-cyan-200'
                    }`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    {/* Popular Badge */}
                    {pack.popular && (
                      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
                        <div className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full text-sm font-medium shadow-lg animate-pulse">
                          <Star className="w-4 h-4" fill="currentColor" />
                          {isEnglish ? 'Most popular' : 'Le plus populaire'}
                        </div>
                      </div>
                    )}

                    {/* Package Image - Fixed height for all cards (30-35% of card) */}
                    <div className="relative w-full h-48 sm:h-56 md:h-60 overflow-hidden">
                      <img 
                        src={pack.image} 
                        alt={pack.name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>

                    {/* Card Content */}
                    <div className="p-6 md:p-8">
                      {/* 1. Package Title */}
                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">
                          {pack.name}
                        </h3>
                        <p className="text-slate-500 text-sm">
                          {pack.description}
                        </p>
                      </div>
                      
                      {/* 2. Main Price */}
                      <div className="flex items-center justify-center gap-2 mb-6">
                        <span className="text-2xl text-slate-400 line-through">
                          {pack.originalPrice}€
                        </span>
                        <span className="text-4xl font-bold text-cyan-600">
                          {pack.packPrice}€
                        </span>
                      </div>

                      {/* 3. INCLUS DANS CE PACK - Activities List */}
                      <div className="space-y-3 mb-6">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                          {isEnglish ? 'Included in this pack:' : 'Inclus dans ce pack :'}
                        </p>
                        {pack.activities.map((activity, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center p-3 bg-slate-50 rounded-xl hover:bg-cyan-50 transition-colors cursor-default group"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                                <Check className="w-3 h-3 text-cyan-600" />
                              </div>
                              <span className="text-slate-700 font-medium">{activity.name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* 4. ENTIRE Stripe Block - BELOW activities, ABOVE WhatsApp */}
                      <div className="w-full text-center p-4 bg-emerald-50 rounded-xl relative z-10 mb-4">
                        <p className="text-sm font-semibold text-emerald-700 mb-1">
                          {isEnglish ? `Book now for ${pack.depositPrice} €` : `Réservez maintenant pour ${pack.depositPrice} €`}
                        </p>
                        <p className="text-xs text-slate-500 mb-4">
                          {isEnglish ? `Balance on site: ${pack.remainingPrice} €` : `Solde à régler sur place : ${pack.remainingPrice} €`}
                        </p>
                        
                        {/* Stripe CTA button */}
                        <a
                          href={pack.stripeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="w-full block rounded-full py-3 transition-all duration-300 text-white shadow-lg hover:shadow-xl active:scale-95 font-medium touch-manipulation mb-2 text-center"
                          style={{ 
                            backgroundColor: '#635BFF',
                            WebkitTapHighlightColor: 'rgba(99, 91, 255, 0.3)'
                          }}
                        >
                          {isEnglish ? 'Book this pack' : 'Réserver ce pack'}
                        </a>
                        <p className="text-xs text-slate-500 text-center">
                          🔒 {isEnglish ? 'Secure payment · Deposit only' : 'Paiement sécurisé · Acompte uniquement'}
                        </p>
                      </div>

                      {/* 5. WhatsApp CTA (SECONDARY) - BELOW Stripe */}
                      <Button
                        asChild
                        className={`w-full rounded-full py-6 transition-all duration-300 ${
                          pack.popular
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-200 hover:shadow-emerald-300'
                            : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md'
                        }`}
                      >
                        <a href={whatsappPackLink} target="_blank" rel="noopener noreferrer">
                          <WhatsAppIcon className="w-4 h-4 mr-2" />
                          {isEnglish ? '💬 Questions? WhatsApp' : '💬 Questions ? WhatsApp'}
                        </a>
                      </Button>
                      {/* Founder photo */}
                      <div className="flex justify-center mt-3">
                        <img
                          src="https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,c_fill,w_120,h_120/Maximehurghadaexcursionfounder_rohsvv.jpg"
                          alt="Maxime"
                          className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-slate-500 mt-8">
              {isEnglish ? 'Prefer to book a single activity?' : 'Vous préférez réserver une seule activité ?'}{' '}
              <Link to={`${langPrefix}/#excursions`} className="text-cyan-600 hover:underline font-medium">
                {isEnglish ? 'View individual activities' : 'Voir les activités à l\'unité'}
              </Link>
            </p>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-12 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium mb-4">
                {isEnglish ? 'Simple and fast' : 'Simple et rapide'}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
                {isEnglish ? 'How does it work?' : 'Comment ça marche ?'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {[
                { 
                  icon: WhatsAppIcon, 
                  step: '1', 
                  title: isEnglish ? 'You message me' : "Vous m'écrivez", 
                  desc: isEnglish ? 'Contact me on WhatsApp with the pack you\'re interested in' : 'Contactez-moi sur WhatsApp avec le pack qui vous intéresse',
                  color: 'bg-cyan-500'
                },
                { 
                  icon: CheckCircle, 
                  step: '2', 
                  title: isEnglish ? 'You receive everything' : 'Vous recevez tout', 
                  desc: isEnglish ? 'Schedules, meeting points and practical info for each activity' : 'Horaires, points de RDV et infos pratiques pour chaque activité',
                  color: 'bg-emerald-500'
                }
              ].map((item, index) => (
                <div 
                  key={index} 
                  className="relative text-center animate-in fade-in slide-in-from-bottom duration-500"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className={`w-20 h-20 ${item.color} rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6 hover:scale-110 hover:rotate-3 transition-all duration-300`}>
                    <item.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center text-sm font-bold text-slate-600 shadow-sm">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { 
                  icon: WhatsAppIcon, 
                  title: isEnglish ? 'English support' : 'Contact francophone', 
                  desc: isEnglish ? 'I assist you at every step' : 'Je vous accompagne en français à chaque étape' 
                },
                { 
                  icon: Users, 
                  title: isEnglish ? 'Reliable local partners' : 'Partenaires locaux fiables', 
                  desc: isEnglish ? 'Tested and selected excursions' : 'Excursions testées et sélectionnées' 
                },
                { 
                  icon: CreditCard, 
                  title: isEnglish ? 'Transparent payment' : 'Paiement transparent', 
                  desc: isEnglish ? '20% deposit online, balance on site' : 'Acompte 20% en ligne, solde sur place' 
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:border-cyan-200 hover:bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-in fade-in slide-in-from-bottom duration-500"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-slate-200 text-slate-700 rounded-full text-sm font-medium mb-4">
                <HelpCircle className="w-4 h-4 inline mr-1" />
                {isEnglish ? 'Frequently asked questions' : 'Questions fréquentes'}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
                {isEnglish ? 'FAQ – Excursion Packages' : "FAQ – Packs d'excursions"}
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {currentFaq.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-slate-100 px-6 hover:border-cyan-200 transition-colors data-[state=open]:border-cyan-300 data-[state=open]:bg-cyan-50/50 animate-in fade-in slide-in-from-bottom duration-500"
                  style={{ animationDelay: `${index * 75}ms` }}
                >
                  <AccordionTrigger className="text-left text-slate-800 font-semibold py-5 hover:no-underline hover:text-cyan-600 transition-colors [&[data-state=open]]:text-cyan-600">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Reassurance */}
            <div className="mt-10 p-6 bg-white rounded-2xl border border-slate-100 text-center animate-in fade-in slide-in-from-bottom duration-500">
              <p className="text-slate-600 italic">
                {isEnglish 
                  ? '"I am based in Hurghada, and I personally assist you for a simple and stress-free booking."'
                  : '"Je suis français, basé à Hurghada, et je vous accompagne personnellement pour une réservation simple et sans stress."'}
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-12 md:py-20 bg-gradient-to-b from-white to-cyan-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100 text-center animate-in fade-in zoom-in duration-500">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                {isEnglish ? 'Ready to book your package?' : 'Prêt à réserver votre pack ?'}
              </h2>
              <p className="text-slate-600 mb-8 max-w-xl mx-auto">
                {isEnglish 
                  ? 'Contact me on WhatsApp to confirm availability and finalize your booking.'
                  : 'Contactez-moi sur WhatsApp pour confirmer les disponibilités et finaliser votre réservation.'}
              </p>
              <Button
                asChild
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-10 py-6 rounded-full shadow-xl shadow-emerald-200 transition-all duration-300 hover:shadow-emerald-300 hover:scale-105"
              >
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon className="w-5 h-5 mr-2" />
                  {isEnglish ? 'Book a package' : 'Réserver un pack'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <p className="text-sm text-slate-400 mt-4">
                {isEnglish ? 'Fast response guaranteed • No commitment' : 'Réponse rapide garantie • Aucun engagement'}
              </p>
            </div>
          </div>
        </section>
      </main>

      <StickyWhatsAppMobile />
      <Footer />
    </div>
  );
};

export default PacksPage;
